// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useApp, navigate } from '../store/AppStore';
import { useAuth } from '../store/AuthContext';
import { ScreenShell } from '../components/WebShell';
import { NewTicketModal, FiltersDrawer } from '../components/Modals';
import ConfirmDialog from '../components/ConfirmDialog';
import SuccessDialog from '../components/SuccessDialog';
import { ticketsService } from '../services/tickets/tickets.service';
import { agentsService } from '../services/agents/agents.service';
import { apiClient } from '../services/api-client';
import { PLATFORM_ORG } from '../api-endpoints/api-endpoints';
import { mapApiTicket } from '../store/dataMapper';
import CommonSearch from '../components/Common_Search/Common_Search_Component';
import { useResponsive } from '../theme/responsive';
import { slaLabel, classifySla } from '../components/SlaState';

// Exact arrival / resolution stamp from an API ISO string.
function fullTs(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// Human duration between two ISO timestamps ("2h 14m", "3d 5h").
function durationLabel(startISO?: string | null, endISO?: string | null): string {
  if (!startISO || !endISO) return '—';
  const a = new Date(startISO).getTime(), b = new Date(endISO).getTime();
  if (isNaN(a) || isNaN(b) || b < a) return '—';
  let mins = Math.floor((b - a) / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  let hrs = Math.floor(mins / 60); mins = mins % 60;
  if (hrs < 24) return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24); hrs = hrs % 24;
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
}

const CLOCK_PATH = '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>';
const CHECKC_PATH = '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>';

// Split an ISO timestamp into a date line + time line.
function tsParts(iso?: string | null): { date: string; time: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
  };
}

// Date over time, with a leading icon — used identically for Arrived & Resolved.
function TimeCell({ iso, icon, color }: { iso?: string | null; icon: string; color?: string }) {
  const p = tsParts(iso);
  if (!p) return <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color || 'var(--ink-4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.85 }} dangerouslySetInnerHTML={{ __html: icon }} />
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: color || 'var(--ink-2)', whiteSpace: 'nowrap' }}>{p.date}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>{p.time}</span>
      </span>
    </span>
  );
}

const AGENT_GRADS = [
  'linear-gradient(135deg,#0ea5e9,#6366f1)', 'linear-gradient(135deg,#f97316,#db2777)',
  'linear-gradient(135deg,#10b981,#0ea5e9)', 'linear-gradient(135deg,#a855f7,#ec4899)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)', 'linear-gradient(135deg,#84cc16,#22c55e)',
  'linear-gradient(135deg,#f59e0b,#dc2626)',
];
function gradFor(str: string): string {
  let h = 0; for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return AGENT_GRADS[Math.abs(h) % AGENT_GRADS.length];
}
function initialsOf(name: string): string {
  return (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
}

// ─── AssignCell — inline assignee with a portaled picker ──────────────────────
function AssignCell({ ticket }: { ticket: any }) {
  const { reloadTicket, toast } = useApp();
  const { user, org } = useAuth();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const btnRef = useRef<any>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: any) => { if (e.target?.closest?.('[data-assign-keep]')) return; setOpen(false); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const openMenu = async (e: any) => {
    e.stopPropagation();
    const r = btnRef.current?.getBoundingClientRect?.();
    if (r) setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 280)) });
    setOpen(true);
    const [agentsRes, membersRes] = await Promise.allSettled([
      agentsService.list(),
      org?.id ? apiClient.get(PLATFORM_ORG.members(org.id)) : Promise.resolve(null),
    ]);
    let list: any[] = [];
    if (agentsRes.status === 'fulfilled') { const raw = (agentsRes.value as any)?.data || agentsRes.value || []; list = Array.isArray(raw) ? raw : []; }
    const haveIds = new Set(list.map((a: any) => a.platformUserId).filter(Boolean));
    if (membersRes.status === 'fulfilled' && membersRes.value) {
      const raw = (membersRes.value as any)?.data || membersRes.value || [];
      const members = (Array.isArray(raw) ? raw : [])
        .filter((m: any) => (m.status ? m.status === 'active' : true) && m.user_id && !haveIds.has(m.user_id))
        .map((m: any) => ({ platformUserId: m.user_id, displayName: [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ').trim() || (m.user?.email || '').split('@')[0] || m.user_id, email: m.user?.email }));
      list = [...list, ...members];
    }
    if (user?.id && !list.some((a: any) => a.platformUserId === user.id)) {
      list = [{ platformUserId: user.id, displayName: ([user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || 'Me') + ' (me)' }, ...list];
    }
    setAgents(list);
  };

  const assign = async (agent: any) => {
    setOpen(false);
    try {
      await ticketsService.assign(ticket._apiId, agent?.platformUserId);
      await reloadTicket(ticket.id);
    } catch (e: any) {
      const notMember = e?.response?.status === 403 || e?.response?.data?.error === 'MEMBER_NOT_FOUND';
      if (notMember && agent?.platformUserId) {
        try {
          await agentsService.create({ platformUserId: agent.platformUserId, displayName: (agent.displayName || agent.email || 'Agent').replace(/\s*\(me\)$/, ''), email: agent.email } as any);
        } catch (err: any) { if (err?.response?.status !== 409) { toast('Failed to assign agent', 'danger'); return; } }
        try { await ticketsService.assign(ticket._apiId, agent.platformUserId); await reloadTicket(ticket.id); return; } catch { toast('Failed to assign agent', 'danger'); return; }
      }
      toast('Failed to assign agent', 'danger');
    }
  };

  const unassign = async (e: any) => {
    e?.stopPropagation?.(); setOpen(false);
    try { await ticketsService.assign(ticket._apiId, undefined); await reloadTicket(ticket.id); }
    catch { toast('Failed to unassign', 'danger'); }
  };

  const rowStyle: any = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 9 };
  const av = (seed: string): any => ({ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 10, fontWeight: 700, background: gradFor(seed) });
  const a = ticket.assignee;

  const menu = open ? ReactDOM.createPortal(
    <div data-assign-keep style={{ position: 'fixed', top: pos?.top ?? 60, left: pos?.left ?? 16, zIndex: 2000, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 12px 32px -8px rgba(10,10,10,.22), 0 2px 6px rgba(10,10,10,.06)', width: 264, padding: 6, maxHeight: 320, overflowY: 'auto' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: 'var(--ink-4)', textTransform: 'uppercase', padding: '6px 10px 4px' }}>Assign to</div>
      <button style={rowStyle} onClick={unassign}>
        <span style={{ ...av('x'), background: 'transparent', color: 'var(--ink-4)', border: '1px dashed var(--line-strong)' }}>—</span>
        <span style={{ flex: 1, color: 'var(--ink-2)', fontSize: 13 }}>Unassigned</span>
      </button>
      {agents.map((ag: any) => {
        const name = (ag.displayName || ag.name || ag.platformUserId || 'Agent').replace(/\s*\(me\)$/, '');
        return (
          <button key={ag.platformUserId} style={rowStyle} onClick={(e) => { e.stopPropagation(); assign(ag); }}>
            <span style={av(ag.platformUserId || name)}>{initialsOf(name)}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
              {name}{ag.platformUserId === user?.id ? ' (you)' : ''}
            </span>
          </button>
        );
      })}
    </div>, document.body) : null;

  return (
    <div data-assign-keep style={{ position: 'relative' }}>
      <button ref={btnRef} onClick={openMenu} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        {a ? (
          <>
            <span style={av(ticket.assignedTo || a.name)}>{a.initials}</span>
            <span className="t-truncate" style={{ fontSize: 12.5, maxWidth: 120 }}>{a.name}</span>
          </>
        ) : (
          <span className="t-chip" style={{ borderStyle: 'dashed', color: 'var(--accent)', borderColor: 'var(--accent-line)' }}>+ Assign</span>
        )}
      </button>
      {menu}
    </div>
  );
}

const VIEW_META: Record<string, { title: string; sub: string }> = {
  all:      { title: 'Tickets',          sub: 'All open + recent across every channel.' },
  open:     { title: 'My open tickets',  sub: 'Assigned to you · not yet resolved.' },
  pending:  { title: 'Awaiting reply',   sub: 'Customer is waiting on a response from us.' },
  resolved: { title: 'Resolved today',  sub: 'Closed-out tickets from the last 24h.' },
  bounced:  { title: 'Bounced',          sub: 'Tickets where at least one outbound email failed delivery.' },
};

const STATUS_VIEWS = [
  { label: 'All',            key: 'all',      path: '/tickets' },
  { label: 'Open',           key: 'open',     path: '/tickets/view/open' },
  { label: 'Awaiting reply', key: 'pending',  path: '/tickets/view/pending' },
  { label: 'Resolved',       key: 'resolved', path: '/tickets/view/resolved' },
  { label: 'Bounced',        key: 'bounced',  path: '/tickets/view/bounced' },
];
const CHANNEL_OPTS = ['any', 'email', 'api', 'web'];

// Friendly, view-aware empty states.
const VIEW_EMPTY: Record<string, { title: string; sub: string; icon: string }> = {
  all:      { title: 'No tickets yet',          sub: 'New tickets will show up here as they arrive.',                icon: '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>' },
  open:     { title: "You're all caught up",     sub: 'No open tickets need your attention right now.',              icon: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>' },
  pending:  { title: 'Nothing awaiting a reply',  sub: 'Tickets waiting on the customer will appear here.',            icon: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>' },
  resolved: { title: 'Nothing resolved today',    sub: 'Tickets resolved in the last 24 hours show up here.',          icon: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>' },
  bounced:  { title: 'No delivery issues',        sub: 'Tickets with a failed outbound email would appear here.',      icon: '<path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>' },
};

function EmptyTickets({ filter, searching, q, loading }: { filter: string; searching: boolean; q: string; loading?: boolean }) {
  const m = searching
    ? { title: `No results for "${q}"`, sub: 'Try a different search term.', icon: '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>' }
    : (VIEW_EMPTY[filter] || VIEW_EMPTY.all);
  return (
    <div className="sk-empty">
      <div className="sk-empty-icon">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: loading ? VIEW_EMPTY.pending.icon : m.icon }} />
      </div>
      <div className="sk-empty-title">{loading ? 'Loading…' : m.title}</div>
      <div className="sk-empty-sub">{loading ? 'Fetching the latest tickets.' : m.sub}</div>
    </div>
  );
}

const STATUS_CHIP: Record<string, string> = {
  open: 't-chip--info', pending: 't-chip--warn', resolved: 't-chip--success', closed: 't-chip--neutral',
};

// The list filters on the 4-bucket `status`, but the chip should show the real
// backend status (e.g. "In progress", "On hold") so a status change is visibly
// reflected on the dashboard — not silently collapsed to "Open".
function statusLabel(t: any): string {
  const raw = (t._apiStatus || t.status || '').replace(/_/g, ' ');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
const PRIORITY_CHIP: Record<string, string> = {
  urgent: 't-chip--danger', high: 't-chip--danger', medium: 't-chip--warn', low: 't-chip--neutral',
};
const CH_SVG: Record<string, string> = {
  email: '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>',
  api:   '<rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>',
  web:   '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
};
const WARN_PATH = '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>';
const FILTER_PATH = '<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>';
const PLUS_PATH = '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>';

/**
 * SKTICK-DB-001 Surface D — SLA chip class for a row.
 *
 * The label and the classification come from the SHARED helpers in
 * components/SlaState, which read the server's resolved `sla` — already net of
 * pause. This file previously carried slaText()/slaStyle(), which derived a
 * countdown from the due date and the wall clock; they ignored pause, so a
 * `pending` or `on_hold` ticket counted down while its clock was stopped. They
 * were also dead code — this table never had an SLA column. Both are gone.
 */
const SLA_CHIP = {
  breached: 't-chip--danger',
  approaching: 't-chip--warn',
  paused: 't-chip--neutral',
  running: 't-chip--neutral',
  none: 't-chip--neutral',
};

function hasBounce(t) {
  if (t.flagged === 'bounce') return true;
  return t.comments.some(c =>
    c.type === 'system-bounce' ||
    (c.delivery && (c.delivery.state === 'bounced' || c.delivery.state === 'partial'))
  );
}

export function TicketsListScreen({ view: viewProp = 'all' }) {
  // Scope the dataset to the active space (∩ server-enforced ACL). Crossing the
  // PUBLIC/INTERNAL boundary only happens via the sidebar space switcher.
  const { tickets, visibleTickets, addTicket, activeSpace, isInternalSpace, deleteTicket, isAssignmentOnly } = useApp();
  const { tableMode } = useResponsive();
  const activeInternal = activeSpace?.type === 'INTERNAL';
  const [filter, setFilter] = useState(viewProp);
  const [channelFilter, setChannelFilter] = useState('any');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [adv, setAdv] = useState({ priority: [], assignee: [], delivery: [], inbox: [], opened: [], tags: [], nodeId: '' });

  // Delete flow: confirm → (API) → success. `pendingDelete` holds the ticket
  // awaiting confirmation; `deleting` guards the in-flight request; `deleted`
  // carries the just-removed ticket so the success dialog can name it.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(null);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const ok = await deleteTicket(pendingDelete.id);
    setDeleting(false);
    if (ok) {
      setDeleted(pendingDelete);   // open success dialog
      setPendingDelete(null);      // close confirm dialog
    } else {
      setPendingDelete(null);      // failure already toasted by the store
    }
  };

  // ── Search state ───────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q.trim()) { setSearchResults(null); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await ticketsService.list({ q: q.trim(), limit: 50 });
        const raw = res?.data || [];
        setSearchResults(raw.map(mapApiTicket));
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
  }, []);

  useEffect(() => { setFilter(viewProp); }, [viewProp]);

  const clearAdv = () => setAdv({ priority: [], assignee: [], delivery: [], inbox: [], opened: [], tags: [] });
  const meta = VIEW_META[filter] ?? VIEW_META.all;
  const advCount = Object.values(adv).reduce((s: any, arr: any) => s + (arr?.length || 0), 0);

  // Use search results when searching, else the active-space-scoped tickets.
  const displayTickets = searchResults !== null ? searchResults : visibleTickets;
  const filtered = displayTickets.filter(t => {
    if (searchResults !== null) return true; // search results already filtered by API
    if (filter === 'bounced') { if (!hasBounce(t)) return false; }
    else if (filter !== 'all') { if (t.status !== filter) return false; }
    if (channelFilter !== 'any' && t.channel !== channelFilter) return false;
    // Apply advanced filters
    if (adv.priority?.length > 0 && !adv.priority.includes(t.priority)) return false;
    if (adv.tags?.length > 0 && !adv.tags.some((tag: string) => t.tags?.includes(tag))) return false;
    if (adv.nodeId && t.nodeId !== adv.nodeId) return false;
    return true;
  });

  /**
   * SKTICK-DB-001 Surface C — the Outlet column and the node filter appear
   * only when this org actually uses node scoping. `node_id` is null for every
   * product that does not, so an always-on column would be a dead dash for
   * most tenants.
   *
   * Derived from the tickets ALREADY LOADED, which RLS has already narrowed —
   * so this never reveals that nodes exist outside what the viewer can see.
   */
  const anyNode = displayTickets.some((t: any) => !!t.nodeId);

  /**
   * The node picker is a flat, searchable list of nodes the viewer can already
   * see — deliberately NOT a tree browser. This product does not resolve the
   * SK Platform hierarchy, and a picker enumerating 1,030 outlets would itself
   * be a disclosure.
   */
  const nodeOptions = Array.from(
    new Map(
      displayTickets
        .filter((t: any) => !!t.nodeId)
        .map((t: any) => [t.nodeId, t.nodeLabel || t.nodeId]),
    ).entries(),
  ).sort((a, b) => String(a[1]).localeCompare(String(b[1])));

  return (
    <ScreenShell crumbs={[]}>
      {/* Page header */}
      <div className="sk-page-head">
        <div>
          <div className="sk-page-head-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>{searchQuery ? `Search: "${searchQuery}"` : meta.title}</span>
            {!searchQuery && activeInternal && (
              <span className="ilock ilock--lg">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>' }} />
                <span>INTERNAL</span>
                <span className="ilock-key">{(activeSpace?.key || '').toUpperCase()}</span>
              </span>
            )}
          </div>
          <div className="sk-page-head-sub">
            {searchLoading
              ? 'Searching…'
              : `${filtered.length} of ${searchResults !== null ? filtered.length : visibleTickets.length} matching · ${searchResults !== null ? 'search results' : ((activeSpace ? activeSpace.name + ' · ' : '') + meta.sub)}`}
          </div>
        </div>
        <div className="sk-page-head-actions">
          <button
            className="t-btn t-btn--primary"
            onClick={() => setShowNewTicket(true)}
            style={{
              fontSize: 14.5, fontWeight: 700, padding: '12px 24px', borderRadius: 12, gap: 9,
              border: 'none', color: '#fff',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              boxShadow: '0 12px 26px -8px rgba(79,70,229,0.55)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 16px 32px -8px rgba(79,70,229,0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 12px 26px -8px rgba(79,70,229,0.55)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" dangerouslySetInnerHTML={{ __html: PLUS_PATH }} />
            New ticket
          </button>
        </div>
      </div>

      {/* Search — above the list, like Inbox */}
      <div className="sk-search-row">
        <div className="sk-search-row-inner">
          <CommonSearch value={searchQuery} onChange={handleSearch} placeholder="Search tickets…" />
        </div>
      </div>

      {/* Filter / tab bar */}
      <div className="sk-filter-bar">
        {STATUS_VIEWS.map(sv => (
          <button key={sv.key}
            className={`sk-filter-chip${filter === sv.key ? ' is-active' : ''}`}
            onClick={() => { setFilter(sv.key); navigate(sv.path); }}>
            {sv.label}
          </button>
        ))}
        <span className="sk-filter-divider" />
        <span className="sk-filter-label">Channel:</span>
        {CHANNEL_OPTS.map(ch => (
          <button key={ch}
            className={`sk-ch-chip${channelFilter === ch ? ' is-active' : ''}`}
            onClick={() => setChannelFilter(ch)}>
            {ch.charAt(0).toUpperCase() + ch.slice(1)}
          </button>
        ))}

        {/*
          SKTICK-DB-001 Surface C — node filter.

          A FLAT, SEARCHABLE LIST, never a tree browser: SK Tickets does not
          resolve the SK Platform hierarchy and must not appear to. The options
          come from tickets already loaded, so it enumerates only nodes the
          viewer can already see.

          Hidden entirely for an assignment-only user (§4): with one outlet it
          is noise, and a picker listing 1,030 outlets would be a disclosure.
          `anyNode` keeps it off every tenant that does not use node scoping.
        */}
        {anyNode && isAssignmentOnly !== true && nodeOptions.length > 0 && (
          <>
            <span className="sk-filter-divider" />
            <span className="sk-filter-label">Outlet:</span>
            <select
              className="sk-ch-chip"
              value={adv.nodeId || ''}
              aria-label="Filter by outlet"
              onChange={(e) => setAdv({ ...adv, nodeId: e.target.value })}
            >
              <option value="">Any</option>
              {nodeOptions.map(([id, label]) => (
                <option key={String(id)} value={String(id)}>{String(label)}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* List — table on desktop, stacked cards on mobile/tablet */}
      {tableMode === 'cards' ? (
        <div className="sk-table-wrap">
          {filtered.length === 0 ? (
            <div className="sk-table-card"><EmptyTickets filter={filter} searching={searchQuery.trim().length > 0} q={searchQuery} loading={searchLoading} /></div>
          ) : (
            <div className="sk-card-list">
              {filtered.map((t, i) => {
                const sCls = STATUS_CHIP[t.status] ?? 't-chip--neutral';
                const pCls = PRIORITY_CHIP[t.priority] ?? 't-chip--neutral';
                return (
                  <div className="sk-data-card" key={t.id} onClick={() => navigate('/tickets/' + t.id)}>
                    <div className="sk-data-card-head">
                      <span className="t-dim" style={{ fontSize: 12, fontWeight: 600 }}>{i + 1}.</span>
                      <span className="t-mono t-dim" style={{ fontSize: 12 }}>{t.id}</span>
                      <span className="sk-data-card-title">{t.subject}</span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Status</span>
                      <span className="sk-data-card-value" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', whiteSpace: 'normal', overflow: 'visible' }}>
                        <span className={`t-chip ${sCls}`}><span className="t-chip-dot" />{statusLabel(t)}</span>
                        <span className={`t-chip ${pCls}`}><span className="t-chip-dot" />{t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}</span>
                      </span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Requester</span>
                      <span className="sk-data-card-value">{t.customer.name}</span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Category</span>
                      <span className="sk-data-card-value">{t.category}</span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Assignee</span>
                      <span className="sk-data-card-value">{t.assignee ? t.assignee.name : 'Unassigned'}</span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Arrived</span>
                      <span className="sk-data-card-value">{fullTs(t.createdAtISO)}</span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Resolved</span>
                      <span className="sk-data-card-value">{t.resolvedAtLabel || '—'}</span>
                    </div>
                    <div className="sk-data-card-row">
                      <span className="sk-data-card-label">Time to resolve</span>
                      <span className="sk-data-card-value">{t.resolvedAtISO ? durationLabel(t.createdAtISO, t.resolvedAtISO) : '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <div className="sk-table-wrap">
        <div className="sk-table-card">
          <table className="t-table">
            <thead>
              <tr>
                <th style={{ width: 28, padding: '9px 6px' }} />
                <th style={{ width: 52 }}>S.No</th>
                <th style={{ width: 90 }}>Ticket</th>
                <th>Subject</th>
                <th style={{ width: 120 }}>Category</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 100 }}>Priority</th>
                <th style={{ width: 150 }}>SLA</th>
                {anyNode && <th style={{ width: 140 }}>Outlet</th>}
                <th style={{ width: 150 }}>Requester</th>
                <th style={{ width: 150 }}>Assignee</th>
                <th style={{ width: 140 }}>Arrived</th>
                <th style={{ width: 140 }}>Resolved</th>
                <th style={{ width: 120 }}>Time to resolve</th>
                <th style={{ width: 64, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr style={{ cursor: 'default' }}>
                  <td colSpan={anyNode ? 15 : 14} style={{ padding: 0 }}>
                    <EmptyTickets filter={filter} searching={searchQuery.trim().length > 0} q={searchQuery} loading={searchLoading} />
                  </td>
                </tr>
              ) : filtered.map((t, i) => {
                const bounce = hasBounce(t);
                const sCls = STATUS_CHIP[t.status] ?? 't-chip--neutral';
                const pCls = PRIORITY_CHIP[t.priority] ?? 't-chip--neutral';
                const chSvg = CH_SVG[t.channel] || CH_SVG.web;
                // Internal rows get a teal left rail + a compact lock+key before
                // the subject, and the requester renders as an employee chip.
                const rowInternal = isInternalSpace((t as any).spaceId);
                return (
                  <tr key={t.id} className={rowInternal ? 'irow-rail' : undefined} onClick={() => navigate('/tickets/' + t.id)}>
                    <td style={{ width: 28, padding: '11px 6px', textAlign: 'center' }}>
                      {bounce && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="var(--warn)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          dangerouslySetInnerHTML={{ __html: WARN_PATH }} />
                      )}
                    </td>
                    <td>
                      <span className="t-dim" style={{ fontSize: 12.5, fontWeight: 600 }}>{i + 1}</span>
                    </td>
                    <td>
                      <span className="t-mono t-dim" style={{ fontSize: 12 }}>{t.id}</span>
                    </td>
                    <td style={{ maxWidth: 460 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {rowInternal && (
                          <span title="Internal ticket" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flex: 'none', color: 'var(--internal-ink)' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>' }} />
                            <span className="t-mono" style={{ fontSize: 10.5, fontWeight: 700 }}>{(activeSpace?.key || '').toUpperCase()}</span>
                          </span>
                        )}
                        <span className="t-truncate t-strong" style={{ fontSize: 13.5 }}>{t.subject}</span>
                        {t.vertical && (
                          <span
                            title={`Raised via ${t.vertical}`}
                            style={{ flex: 'none', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 6px', borderRadius: 5, background: 'var(--internal-bg, #eef1f6)', color: 'var(--internal-ink, #55607a)' }}
                          >
                            {t.vertical}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="t-chip t-chip--accent">
                        <span className="t-chip-dot" />{t.category}
                      </span>
                    </td>
                    <td>
                      <span className={`t-chip ${sCls}`}>
                        <span className="t-chip-dot" />
                        {statusLabel(t)}
                      </span>
                    </td>
                    <td>
                      <span className={`t-chip ${pCls}`}>
                        <span className="t-chip-dot" />
                        {t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}
                      </span>
                    </td>
                    <td>
                      {/* Server-resolved. A paused clock reads as paused. */}
                      {t.sla && slaLabel(t.sla) ? (
                        <span className={`t-chip ${SLA_CHIP[classifySla(t.sla)]}`}>
                          {slaLabel(t.sla)}
                        </span>
                      ) : (
                        <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>
                      )}
                    </td>
                    {anyNode && (
                      <td>
                        {/* A label, never a link — this product does not resolve the tree. */}
                        {t.nodeId ? (
                          <span className="t-truncate t-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}
                                title={t.nodeLabel || t.nodeId}>
                            {t.nodeLabel || t.nodeId}
                          </span>
                        ) : (
                          <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>
                        )}
                      </td>
                    )}
                    <td>
                      {rowInternal ? (
                        <span className="t-emp t-truncate" title="Employee · resolved from SK People" style={{ maxWidth: 160 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/>' }} />
                          {t.customer.name}
                        </span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center',
                            background: t.customer.color || 'var(--paper-3)', color: '#fff',
                            fontSize: 9, fontWeight: 700, flexShrink: 0,
                          }}>{t.customer.initials}</div>
                          <span className="t-truncate" style={{ fontSize: 12.5 }}>{t.customer.name}</span>
                        </div>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <AssignCell ticket={t} />
                    </td>
                    <td>
                      <TimeCell iso={t.createdAtISO} icon={CLOCK_PATH} color="var(--success)" />
                    </td>
                    <td>
                      <TimeCell iso={t.resolvedAtISO} icon={CHECKC_PATH} color="var(--success)" />
                    </td>
                    <td>
                      {t.resolvedAtISO ? (
                        <span className="t-chip t-chip--success" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {durationLabel(t.createdAtISO, t.resolvedAtISO)}
                        </span>
                      ) : (
                        <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="sk-row-act sk-row-act--danger"
                        title={`Delete ticket ${t.id}`}
                        aria-label={`Delete ticket ${t.id}`}
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(t); }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
                          dangerouslySetInnerHTML={{ __html: '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <NewTicketModal
        open={showNewTicket}
        onCancel={() => setShowNewTicket(false)}
        onCreate={(data: any) => { addTicket(data); setShowNewTicket(false); }}
      />
      <FiltersDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={adv}
        onChange={setAdv}
        onClear={clearAdv}
      />

      <ConfirmDialog
        visible={pendingDelete !== null}
        title="Delete ticket?"
        message={
          pendingDelete
            ? `Ticket ${pendingDelete.id} "${pendingDelete.subject}" will be permanently deleted. This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setPendingDelete(null); }}
      />
      <SuccessDialog
        visible={deleted !== null}
        title="Ticket deleted"
        message={
          deleted
            ? `Ticket ${deleted.id} "${deleted.subject}" has been removed.`
            : undefined
        }
        primaryAction={{ label: 'Done', onPress: () => setDeleted(null) }}
        onClose={() => setDeleted(null)}
      />
    </ScreenShell>
  );
}
